import { UserPreferencesConfig } from 'softcat:config/user-preferences.ts';
import { SoftcatApiCertificate } from 'softcat:types/softcat-api.ts';
import { SearchParameterUtils } from 'softcat:utils/search-parameters.ts';
import { SoftcatApiUtils } from 'softcat:utils/softcat-api.ts';

const activate = async ({ id }: { id: string }): Promise<SoftcatApiCertificate> => {
    const territory = localStorage.getItem(UserPreferencesConfig.TerritoryStorageKey);
    const parameters = new URLSearchParams(SearchParameterUtils.removeEmptySearchParameters({ territory: `${territory}` }));

    return SoftcatApiUtils.post<SoftcatApiCertificate>(`/certificates/action/${id}/activate?${parameters}`);
};

const create = async ({ active, file, name, type }: { active: boolean; file: File; name: string; type: string }): Promise<SoftcatApiCertificate> => {
    const territory = localStorage.getItem(UserPreferencesConfig.TerritoryStorageKey);
    const parameters = new URLSearchParams(SearchParameterUtils.removeEmptySearchParameters({ territory: `${territory}` }));

    const form = new FormData();
    form.append('name', name);
    form.append('active', String(active));
    form.append('type', type);
    form.append('file', file);

    return SoftcatApiUtils.post<SoftcatApiCertificate>(`/certificates?${parameters}`, { body: form });
};

const deactivate = async ({ id }: { id: string }): Promise<SoftcatApiCertificate> => {
    const territory = localStorage.getItem(UserPreferencesConfig.TerritoryStorageKey);
    const parameters = new URLSearchParams(SearchParameterUtils.removeEmptySearchParameters({ territory: `${territory}` }));

    return SoftcatApiUtils.post<SoftcatApiCertificate>(`/certificates/action/${id}/deactivate?${parameters}`);
};

const findAll = async ({ limit = 10, page = 0, query = '', orderBy = '' }: { limit?: number; orderBy?: string; page?: number; query?: string } = {}): Promise<
    SoftcatApiCertificate[]
> => {
    const territory = localStorage.getItem(UserPreferencesConfig.TerritoryStorageKey);
    const parameters = new URLSearchParams(SearchParameterUtils.removeEmptySearchParameters({ territory: `${territory}` }));

    const certs = await SoftcatApiUtils.get<SoftcatApiCertificate[]>(`/certificates?${parameters}`);

    let filtered = [...certs];

    if (query) {
        const pattern = query.toLowerCase();
        filtered = filtered.filter((cert) => {
            // `id` is a string ULID from v2 but a JSON number from v1 (pre-cutover, mirrored
            // traffic still calls the real v1 API) — coerce before comparing so search works
            // against either shape, same as the orderBy comparison below.
            return cert.name.toLowerCase().includes(pattern) || cert.type.toLowerCase().includes(pattern) || String(cert.id).toLowerCase().includes(pattern);
        });
    }

    if (orderBy) {
        const [field, direction] = orderBy.split(':');
        filtered = [...filtered].sort((a, b) => {
            const aValue = String(a[field as keyof SoftcatApiCertificate] ?? '');
            const bValue = String(b[field as keyof SoftcatApiCertificate] ?? '');
            const comparison = aValue.localeCompare(bValue);
            return direction === 'desc' ? -comparison : comparison;
        });
    }

    const start = page * limit;
    return filtered.slice(start, start + limit);
};

const findOne = async ({ id }: { id: string }): Promise<SoftcatApiCertificate> => {
    const territory = localStorage.getItem(UserPreferencesConfig.TerritoryStorageKey);
    const parameters = new URLSearchParams(SearchParameterUtils.removeEmptySearchParameters({ territory: `${territory}` }));

    return SoftcatApiUtils.get<SoftcatApiCertificate>(`/certificates/${id}?${parameters}`);
};

const remove = async ({ id }: { id: string }): Promise<void> => {
    const territory = localStorage.getItem(UserPreferencesConfig.TerritoryStorageKey);
    const parameters = new URLSearchParams(SearchParameterUtils.removeEmptySearchParameters({ territory: `${territory}` }));

    return SoftcatApiUtils.remove(`/certificates/${id}?${parameters}`);
};

export const CertificateService = {
    activate,
    create,
    deactivate,
    findAll,
    findOne,
    remove,
};
